import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

export interface CsvRow {
  status?: string
  SelfserveClientId?: string
  AdvertId?: string
  selfServedClientId?: string
  advertId?: string
  AppleWalletImageUrl?: string
  GoogleWalletImageUrl?: string
  AppleWalletLogoUrl?: string
  GoogleWalletLogoUrl?: string
  [key: string]: string | undefined
}

/**
 * Parses a CSV file and returns the data rows
 */
export const parseCsvFile = async (file: File): Promise<{ data: CsvRow[]; error?: string }> => {
  try {
    const text = await file.text()
    const parsed = Papa.parse<CsvRow>(text, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: header => header.trim(),
      transform: value => value?.trim() || '',
      dynamicTyping: false,
    })

    // Filter out parsing errors that are just about field count mismatches
    const criticalErrors = parsed.errors.filter(
      error => !error.message.includes('Too few fields') && !error.message.includes('Too many fields')
    )

    if (criticalErrors.length > 0) {
      return { data: [], error: `Error parsing CSV: ${criticalErrors[0].message}` }
    }

    // Warn about field count mismatches but don't block
    if (parsed.errors.length > 0 && process.env.NODE_ENV === 'development') {
      console.warn('CSV parsing warnings (field count mismatches are OK):', parsed.errors)
    }

    return { data: parsed.data }
  } catch (err) {
    return { data: [], error: 'Failed to read CSV file' }
  }
}

/**
 * Generates a CSV file with status information appended
 */
export const generateCsvWithStatus = (
  originalData: CsvRow[],
  statuses: Array<{
    index: number
    status: 'success' | 'failed'
    error?: string
    socialLink?: string
    ctvUrl?: string
  }>,
  fileName?: string
): void => {
  // Create a map of index to status
  const statusMap = new Map(statuses.map(s => [s.index, s]))

  // Add status column and response fields to each row
  const dataWithStatus = originalData.map((row, index) => {
    const status = statusMap.get(index)
    return {
      ...row,
      status: status?.status === 'success' ? 'Success' : status?.error || 'Failed',
      'Social Link': status?.socialLink || '',
      'CTV URL': status?.ctvUrl || '',
    }
  })

  // Convert to CSV with UTF-8 BOM for proper Unicode support
  const csv = Papa.unparse(dataWithStatus)
  // Add UTF-8 BOM (Byte Order Mark) to ensure Excel and other programs recognize UTF-8 encoding
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
  const finalFileName = fileName?.replace('.csv', '') || 'bulk-ads-result'
  saveAs(blob, `${finalFileName}_with_status.csv`)
}

interface SourceFileRow {
  'Product Title'?: string
  'Product Collection'?: string
  'Product Image'?: string
  URL?: string
  [key: string]: string | undefined
}

/**
 * Helper function to validate if a string is a valid URL
 */
const isValidUrl = (urlString: string): boolean => {
  if (!urlString || !urlString.trim()) {
    return false
  }
  try {
    const url = new URL(urlString.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Parses a CSV or XLSX file and extracts Product Title, Product Collection, Product Image, and URL columns
 */
export const parseSourceFile = async (
  file: File
): Promise<{
  data: Array<{ title: string; collection: string; url: string; productImage?: string }>
  error?: string
}> => {
  try {
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    let rows: SourceFileRow[] = []

    if (fileExtension === 'csv') {
      // Read file as UTF-8 text (file.text() handles UTF-8 by default in modern browsers)
      const text = await file.text()
      const parsed = Papa.parse<SourceFileRow>(text, {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: header => header.trim(),
        transform: value => value?.trim() || '',
        dynamicTyping: false,
      })
      rows = parsed.data
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      // Process only the first sheet
      const firstSheetName = workbook.SheetNames[0]
      if (!firstSheetName) {
        return { data: [], error: 'Excel file has no sheets.' }
      }
      const worksheet = workbook.Sheets[firstSheetName]
      rows = XLSX.utils.sheet_to_json<SourceFileRow>(worksheet, {
        defval: '',
        raw: false,
      })
    } else {
      return { data: [], error: 'Unsupported file format. Please upload CSV or XLSX file.' }
    }

    // Extract the columns we need
    const seenUrls = new Set<string>()

    const extractedData = rows
      .map(row => {
        const title = row['Product Title'] || ''
        const collection = row['Product Collection'] || ''
        const url = row.URL || ''
        const productImage = row['Product Image'] || ''
        return {
          title: title.trim(),
          collection: collection.trim(),
          url: url.trim(),
          productImage: productImage.trim(),
        }
      })
      .filter(item => {
        // Skip rows with empty URL
        if (!item.url) {
          return false
        }

        // Normalize URL (remove trailing slashes, convert to lowercase for comparison)
        const normalizedUrl = item.url.toLowerCase().replace(/\/$/, '')

        // Skip if URL is duplicate
        if (seenUrls.has(normalizedUrl)) {
          return false
        }

        seenUrls.add(normalizedUrl)
        return true
      })

    if (extractedData.length === 0) {
      return {
        data: [],
        error:
          'No valid rows found with URL values. Please check your file has "Product Title", "Product Collection", and "URL" columns.',
      }
    }

    return { data: extractedData }
  } catch (err) {
    return { data: [], error: `Failed to parse file: ${(err as Error).message}` }
  }
}

/**
 * Generates a CSV file in the format expected by the bulk ad creation system
 */
export const generateBulkAdsCsv = (
  sourceData: Array<{ title: string; collection: string; url: string; productImage?: string }>,
  fileName?: string,
  appleWalletLogoUrl?: string,
  googleWalletLogoUrl?: string,
  gcpImagesMap?: Map<number, { appleImageUrl: string; googleImageUrl: string }>
): File => {
  // Get dates
  // Start date: Tomorrow
  const startDate = new Date()
  startDate.setDate(startDate.getDate() + 1) // Add 1 day to get tomorrow
  const startDateStr = startDate.toISOString().split('T')[0] // Tomorrow in YYYY-MM-DD format

  // End date: 14 Feb 2026
  const endDate = new Date('2026-02-14')
  const endDateStr = endDate.toISOString().split('T')[0] // 2026-02-14

  // Helper function to truncate title to 50 characters (47 + "...")
  const truncateTitle = (title: string): string => {
    if (title.length <= 50) return title
    const truncated = title.substring(0, 47)
    return `${truncated}...`
  }

  // Notification dates in ISO format (UTC)
  const notification3Date = '2026-02-07T10:00:00.000Z' // 1 week before auction - Saturday, 7 Feb 2026, 10:00 UTC
  const notification4Date = '2026-02-12T10:00:00.000Z' // 2 days before auction - Thursday, 12 Feb 2026, 10:00 UTC
  const notification5Date = '2026-02-13T10:00:00.000Z' // 1 day before auction - Friday, 13 Feb 2026, 10:00 UTC
  const notification6Date = '2026-02-14T09:00:00.000Z' // 1 hour before auction - Saturday, 14 Feb 2026, 09:00 UTC

  // Create CSV rows with all required headers and values
  const csvRows = sourceData.map((item, index) => {
    const truncatedTitle = truncateTitle(item.title || 'Ad')

    // Check if we have GCP images for this row
    const gcpImages = gcpImagesMap?.get(index)

    // Use GCP images if available, otherwise Product Image URL if valid, otherwise placeholder
    let walletImageUrl = 'https://placehold.co/624x246.png'
    if (gcpImages?.appleImageUrl && gcpImages?.googleImageUrl) {
      // Use GCP images (they're the same for both wallets in this case)
      walletImageUrl = gcpImages.appleImageUrl
    } else if (item.productImage && isValidUrl(item.productImage)) {
      walletImageUrl = item.productImage
    }

    return {
      Title: truncatedTitle,
      StartDate: startDateStr,
      EndDate: endDateStr,
      PassColor: '#2A2A2A',
      FontColor: '#FFFFFF',
      ClickthroughUrl: item.url,
      GoogleWalletImageUrl: gcpImages?.googleImageUrl || walletImageUrl,
      AppleWalletImageUrl: gcpImages?.appleImageUrl || walletImageUrl,
      AppleWalletLogoUrl: appleWalletLogoUrl || 'https://placehold.co/480x150.png',
      GoogleWalletLogoUrl: googleWalletLogoUrl || 'https://placehold.co/660x660.png',
      'AppleWalletBackFields1.Label': '',
      'AppleWalletBackFields1.Value': item.title || 'Ad',
      'AppleWalletBackFields2.Label': '',
      'AppleWalletBackFields2.Value': item.collection || '',
      'AppleWalletBackFields3.Label': '',
      'AppleWalletBackFields3.Value': `<a href="${item.url}">View Lot</a>`,
      'AppleWalletBackFields4.Label': '',
      'AppleWalletBackFields4.Value': 'Powered by Tickle, the platform that turns ads into offers & rewards!',
      'GoogleWalletBackFields1.Label': 'Lot Name',
      'GoogleWalletBackFields1.Value': item.title || 'Ad',
      'GoogleWalletBackFields2.Label': 'Collection',
      'GoogleWalletBackFields2.Value': item.collection || '',
      'GoogleWalletBackFields3.Label': 'Credit',
      'GoogleWalletBackFields3.Value': 'Powered by Tickle, the platform that turns ads into offers & rewards!',
      'GoogleWalletBackLinks1.Description': 'View Lot',
      'GoogleWalletBackLinks1.Value': item.url,
      // Notification1: Immediately after saving
      'Notification1.Type': 'TIME_AFTER_SAVING',
      'Notification1.Heading': 'Time to register!',
      'Notification1.SubHeading': 'Tap the link in the pass details to register to bid.',
      'Notification1.TimeAfterSavingUnit': 'INSTANT',
      // Notification3: 1 week before auction
      'Notification3.Type': 'SPECIFIC_DATE_AND_TIME',
      'Notification3.Heading': '1 week to go!',
      'Notification3.SubHeading': `Register to bid on ${item.title || 'the auction'} sale.`,
      'Notification3.Time': notification3Date,
      // Notification4: 2 days before auction
      'Notification4.Type': 'SPECIFIC_DATE_AND_TIME',
      'Notification4.Heading': 'Only 2 days left!',
      'Notification4.SubHeading': 'Review your favorite lots and prepare for bidding action.',
      'Notification4.Time': notification4Date,
      // Notification5: 1 day before auction
      'Notification5.Type': 'SPECIFIC_DATE_AND_TIME',
      'Notification5.Heading': '1 day to go!',
      'Notification5.SubHeading': 'Tap the link on your pass to complete registration before the auction.',
      'Notification5.Time': notification5Date,
      // Notification6: 1 hour before auction
      'Notification6.Type': 'SPECIFIC_DATE_AND_TIME',
      'Notification6.Heading': 'Final hour!',
      'Notification6.SubHeading': `Register for ${item.title || 'the auction'} sale before it's too late.`,
      'Notification6.Time': notification6Date,
    }
  })

  // Convert to CSV with UTF-8 BOM for proper Unicode support
  // Use explicit encoding options for Papa.unparse to ensure UTF-8
  const csv = Papa.unparse(csvRows, {
    delimiter: ',',
    newline: '\n',
    header: true,
  })
  // Add UTF-8 BOM (Byte Order Mark) to ensure Excel and other programs recognize UTF-8 encoding
  const BOM = '\uFEFF'
  const csvWithBom = BOM + csv
  const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' })
  const finalFileName = fileName?.replace(/\.(csv|xlsx|xls)$/i, '') || 'bulk-ads-generated'

  // Auto-download the CSV file
  saveAs(blob, `${finalFileName}.csv`)

  // Create a File object from the blob for use in the component (with UTF-8 BOM)
  return new File([csvWithBom], `${finalFileName}.csv`, { type: 'text/csv;charset=utf-8;' })
}
